import { useState, useRef, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import api, { getRefreshState } from '../services/api';
import { mergeToken } from '../utils/chatUtils';
import { isCustomFieldForwardEvent } from '../utils/streamEventRouter';

// Custom error class to prevent fetchEventSource from retrying
class FatalError extends Error {
  constructor(message) {
    super(message);
    this.name = 'FatalError';
  }
}

// Custom error class to signal token refresh is needed
class RefreshRequiredError extends Error {
  constructor(message = 'Refresh required') {
    super(message);
    this.name = 'RefreshRequiredError';
  }
}

/**
 * Custom hook for managing chat SSE streams
 * Handles message streaming, token refresh, and event routing
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.onTokenReceived - Callback for agent_token events
 * @param {Function} config.onDataEvent - Callback for data events (companies, contacts, custom fields, status)
 * @param {Function} config.onStreamStart - Callback when stream starts (receives campaign_id if new campaign)
 * @param {Function} config.onStreamEnd - Callback when stream ends successfully
 * @param {Function} config.onStreamInterrupted - Callback when the connection drops mid-stream due to a
 *   network error. The backend agent continues running; the observer will signal completion.
 *   Show a "reconnecting…" indicator rather than a hard error.
 * @param {Function} config.onError - Callback for hard, non-recoverable errors
 * @param {Function} config.onUnauthorized - Callback for 401 errors (logout)
 * @param {Function} config.fetchUserProfile - Function to refresh user profile after token refresh
 * @param {Function} config.onCreditSpendGate - Credit gate events from merged EventBus (prompt | resolved | blocked)
 * @returns {Object} - Stream control methods and state
 * @returns {Function} returns.sendMessage - Function to send a message through the chat stream
 * @returns {Function} returns.stopStream - Function to stop the current stream
 * @returns {boolean} returns.isStreaming - Whether tokens are actively being streamed
 * @returns {boolean} returns.isThinking - Whether waiting for the first token (initial thinking state)
 * @returns {boolean} returns.isStreamOpen - Whether the stream connection is open
 */
const useChatStream = ({
  onTokenReceived,
  onDataEvent,
  onStreamStart,
  onStreamEnd,
  onStreamInterrupted,
  onError,
  onUnauthorized,
  fetchUserProfile,
  onCreditSpendGate,
}) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isStreamOpen, setIsStreamOpen] = useState(false);
  
  const streamingContentRef = useRef('');
  const abortControllerRef = useRef(null);
  const streamingTimeoutRef = useRef(null);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      // Check if axios interceptor is already refreshing
      const { isRefreshing, refreshPromise } = getRefreshState();
      
      if (isRefreshing && refreshPromise) {
        // Wait for the existing refresh to complete
        console.log('Refresh already in progress, waiting...');
        const { data } = await refreshPromise;
        
        if (!data?.access_token) {
          throw new Error('Refresh response missing access token');
        }
        
        // Token is already updated by axios interceptor, just return it
        return data.access_token;
      }
      
      // No refresh in progress, start our own
      const { data } = await api.post('/api/auth/refresh', {}, {
        withCredentials: true,
        _retry: true
      });

      if (!data?.access_token) {
        throw new Error('Refresh response missing access token');
      }

      localStorage.setItem('token', data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

      if (typeof fetchUserProfile === 'function') {
        await fetchUserProfile();
      }

      return data.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (onUnauthorized) {
        onUnauthorized();
      }
      throw error;
    }
  }, [fetchUserProfile, onUnauthorized]);

  /**
   * Send a message through the chat stream
   * 
   * @param {Object} params - Message parameters
   * @param {string} params.message - The message content
   * @param {string} params.campaignId - Campaign ID (null for new campaigns)
   * @returns {Promise<string|null>} - Returns campaign ID if a new campaign was created
   */
  const sendMessage = useCallback(async ({
    message,
    campaignId,
    templateId = null
  }) => {
    // Reset streaming state
    streamingContentRef.current = '';
    setIsThinking(true);
    setIsStreaming(false);
    setIsStreamOpen(false);
    
    let currentCampaignId = campaignId;

    const executeStream = async (allowRetry = true) => {
      const token = localStorage.getItem('token');
      const anonymousUserId = localStorage.getItem('anonymous_user_id');
      
      // Require either token or anonymous user ID
      if (!token && !anonymousUserId) {
        throw new Error('Authentication required');
      }

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      // Build headers - include either Authorization or X-Anonymous-User-Id
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (anonymousUserId) {
        headers['X-Anonymous-User-Id'] = anonymousUserId;
      }

      try {
        await fetchEventSource(`${apiBase}/api/chat/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message,
            campaign_id: currentCampaignId || null,
            template_id: templateId || null
          }),
          openWhenHidden: true,
          signal: abortControllerRef.current?.signal,

          async onopen(res) {
            if (res.ok && res.status === 200) {
              // Connected to chat stream
              setIsStreamOpen(true);
            } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
              if (res.status === 401) {
                if (allowRetry) {
                  throw new RefreshRequiredError();
                }
                if (onUnauthorized) {
                  onUnauthorized();
                }
                throw new FatalError(`Client error ${res.status}`);
              }
              // Throw FatalError to prevent automatic retries
              throw new FatalError(`Client error ${res.status}`);
            }
          },

          onmessage(event) {
            if (!event.data || event.data.trim() === '') return;
            
            try {
              const data = JSON.parse(event.data);
              
              // Handle start message
              if (data.event === 'start') {
                if (data.data?.campaign_id && !currentCampaignId) {
                  currentCampaignId = data.data.campaign_id;
                  if (onStreamStart) {
                    onStreamStart(currentCampaignId);
                  }
                }
                return;
              }
              
              // Handle agent token streaming
              if (data.event === 'agent_token') {
                const token = data.data?.text || '';
                
                const append = mergeToken(streamingContentRef.current, token);
                streamingContentRef.current += append;
                
                setIsStreaming(true);
                setIsThinking(false);
                
                // Clear any existing timeout
                if (streamingTimeoutRef.current) {
                  clearTimeout(streamingTimeoutRef.current);
                }
                
                // Set a timeout to detect when streaming pauses
                // If no tokens arrive for 300ms, assume streaming has paused
                streamingTimeoutRef.current = setTimeout(() => {
                  setIsStreaming(false);
                }, 300);
                
                if (onTokenReceived) {
                  onTokenReceived(streamingContentRef.current);
                }
                return;
              }
              
              // Handle data events - delegate to parent
              if (
                data.event === 'companies' ||
                data.event === 'contacts'
              ) {
                if (onDataEvent) {
                  onDataEvent(data);
                }
                return;
              }
              
              // Handle status updates
              if (data.event === 'status') {
                if (onDataEvent) {
                  onDataEvent(data);
                }
                return;
              }

              if (data.event === 'credit_usage_warning') {
                return;
              }

              if (data.event === 'credit_spend_gate' && onCreditSpendGate) {
                onCreditSpendGate(data.data || {});
                return;
              }

              // Handle custom field events (companies, contacts, sec_filings)
              if (isCustomFieldForwardEvent(data.event)) {
                if (onDataEvent) {
                  onDataEvent(data);
                }
                return;
              }
              
              // Handle continuing
              if (data.event === 'continuing') {
                return;
              }
              
              // Handle end
              if (data.event === 'end') {
                // Clear streaming timeout
                if (streamingTimeoutRef.current) {
                  clearTimeout(streamingTimeoutRef.current);
                  streamingTimeoutRef.current = null;
                }
                
                setIsStreaming(false);
                setIsThinking(false);
                setIsStreamOpen(false);
                
                if (onStreamEnd) {
                  onStreamEnd(streamingContentRef.current);
                }
                
                // Reset streaming content after a delay
                setTimeout(() => {
                  streamingContentRef.current = '';
                }, 0);
                return;
              }
              
              // Handle error
              if (data.event === 'error') {
                // Clear streaming timeout
                if (streamingTimeoutRef.current) {
                  clearTimeout(streamingTimeoutRef.current);
                  streamingTimeoutRef.current = null;
                }
                
                setIsStreaming(false);
                setIsThinking(false);
                setIsStreamOpen(false);
                
                if (onError) {
                  onError(data.data?.message || 'Unknown error');
                }
                return;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          },

          onclose() {
            // Clear streaming timeout
            if (streamingTimeoutRef.current) {
              clearTimeout(streamingTimeoutRef.current);
              streamingTimeoutRef.current = null;
            }
            
            setIsStreaming(false);
            setIsThinking(false);
            setIsStreamOpen(false);
            
            // Commit any remaining streaming content
            const finalContent = streamingContentRef.current;
            
            if (onStreamEnd) {
              onStreamEnd(finalContent);
            }
            
            // Reset streaming content
            setTimeout(() => {
              streamingContentRef.current = '';
            }, 0);
          },

          onerror(err) {
            // If this is a RefreshRequiredError from onopen, re-throw it for retry logic
            if (err instanceof RefreshRequiredError) {
              throw err;
            }
            
            // Clear streaming timeout
            if (streamingTimeoutRef.current) {
              clearTimeout(streamingTimeoutRef.current);
              streamingTimeoutRef.current = null;
            }
            
            setIsStreaming(false);
            setIsThinking(false);
            setIsStreamOpen(false);
            
            // Commit any remaining streaming content
            const finalContent = streamingContentRef.current;
            setTimeout(() => {
              streamingContentRef.current = '';
            }, 0);

            // Network-level drops (TypeError: network error / ERR_CONNECTION_RESET)
            // are transient. The backend agent keeps running due to
            // continue_agent_on_disconnect=True; the observer will signal when it
            // finishes. Treat this as an interruption, not a hard error.
            const isNetworkDrop = err instanceof TypeError;
            if (isNetworkDrop) {
              if (onStreamInterrupted) {
                onStreamInterrupted(finalContent);
              }
            } else {
              if (onStreamEnd) {
                onStreamEnd(finalContent);
              }
            }
            
            // Prevent fetchEventSource's internal retry loop regardless.
            throw new FatalError('Stream error - no retry');
          }
        });
      } catch (error) {
        if (allowRetry && error instanceof RefreshRequiredError) {
          await refreshAccessToken();
          return executeStream(false);
        }
        throw error;
      }
    };

    try {
      // Create abort controller
      abortControllerRef.current = new AbortController();
      
      await executeStream(true);
      
      return currentCampaignId;
    } catch (error) {
      console.error('Chat stream error:', error);
      
      // Clear streaming timeout
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
        streamingTimeoutRef.current = null;
      }
      
      setIsStreaming(false);
      setIsThinking(false);
      setIsStreamOpen(false);
      
      if (onError && error.name !== 'AbortError') {
        onError(error.message || 'Failed to send message');
      }
      
      throw error;
    }
  }, [onTokenReceived, onDataEvent, onStreamStart, onStreamEnd, onStreamInterrupted, onError, onUnauthorized, refreshAccessToken, onCreditSpendGate]);

  /**
   * Stop the current stream
   */
  const stopStream = useCallback(() => {
    // Clear streaming timeout
    if (streamingTimeoutRef.current) {
      clearTimeout(streamingTimeoutRef.current);
      streamingTimeoutRef.current = null;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsThinking(false);
    setIsStreamOpen(false);
    streamingContentRef.current = '';
  }, []);

  return {
    sendMessage,
    stopStream,
    isStreaming,
    isThinking,
    isStreamOpen,
  };
};

export default useChatStream;

